// Death recap — replay of the last ~5 s of gameplay + killer
// nameplate + SHARE button. Viral hook: players screenshot / share
// the webm clip of their death, especially the spicy ones.
//
// Implementation: ROTATING recorder, CYCLE_MS = 5 s. A MediaRecorder
// runs continuously, auto-rotating every CYCLE_MS (stop + restart).
// Each `stop()` emits a self-contained WebM with proper duration
// metadata baked in — no EBML Cues hackery or `currentTime` trim.
// On death, we wait POST_DEATH_CAPTURE_MS (~1 s) so the killer
// celebration + ragdoll land in-frame, then force-rotate early. The
// cached `latest` blob covers a single cycle = a bounded ~5 s clip
// whose duration the <video> element reads reliably.
//
// Why NOT a life-scoped recorder: MediaRecorder output omits EBML
// Cues, so even after `stop()` you can't seek back through the file
// reliably. Trying to trim a 30 s life-scoped blob via
// `videoEl.currentTime = dur - 5` silently played the whole thing.
// Rotating cycles side-step this by keeping the blob short enough
// that no trim is needed.
//
// The MVP highlight reel uses a SEPARATE rotating recorder; see
// `src/match/moment_recorder.ts`. Both attach to their own
// `canvas.captureStream(30)` from the same WebGL canvas.
//
// Graceful fallback: if MediaRecorder or canvas.captureStream is
// unsupported (older Safari, locked-down WebViews), the text-only
// overlay still shows.

import { shareClip } from './share_clip';

// Rolling-cycle length. Rotating MediaRecorders produce self-contained
// WebM blobs via `stop()` every CYCLE_MS, which means the blob's
// duration metadata is always correct and seeking works. A life-
// scoped recording would be long but is NOT seekable without Cues
// (MediaRecorder omits them) — that's why trying to trim via
// `videoEl.currentTime = duration - 5` silently failed and played
// the whole life. Back to rotating cycles.
const CYCLE_MS = 5000;
const POST_DEATH_CAPTURE_MS = 1000;
// Overlay total on-screen time: 1 s capture wait + 5 s real-time
// playback + 1 s fade-out = 7 s. Playback is 1.0× now — the old
// 0.35× slow-mo stretched a 5 s clip to ~14 s of view time which
// players reported as too long. Cinematic slo-mo can come back as a
// per-kill-grade flourish later (double-kill 0.7×, triple 0.5×).
const SHOW_DURATION_SEC = 7.0;
const FALLBACK_DURATION_SEC = 2.4;
// Text block (ELIMINATED / BY / WEAPON) fade window. 2× faster than
// the original 1–3.5 s spec so the video is clean for screenshots
// sooner. Share button is intentionally EXCLUDED from this fade —
// player needs it clickable for the full recap duration.
const TEXT_FADE_START_SEC = 0.5;
const TEXT_FADE_END_SEC = 1.75;
const HISTORY_MAX = 5;

function pickMime(): string | null {
  // WebM-only. MediaRecorder MP4 output is fragmented MP4 which is
  // not reliably decodable by <video> when concatenated. WebM EBML
  // + Segment + Cluster structure is self-contained on a `stop()`
  // boundary, which is what we rely on here.
  const candidates = [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ];
  for (const m of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(m)) return m;
  }
  return null;
}

function extForMime(mime: string): string {
  return mime.includes('mp4') ? 'mp4' : 'webm';
}

export class DeathRecap {
  private el: HTMLDivElement;
  private videoEl: HTMLVideoElement;
  private titleBlock: HTMLDivElement;
  private shareBtn: HTMLButtonElement;
  private respawnLabel: HTMLDivElement;
  private statusEl: HTMLDivElement | null = null;
  private timer = 0;
  private duration = FALLBACK_DURATION_SEC;

  // ----- Rotating-recorder state -----
  private canvasRef: HTMLCanvasElement | null = null;
  private mime: string | null = null;
  private activeRecorder: MediaRecorder | null = null;
  private activeParts: Blob[] = [];
  private activeStartMs = 0;
  private rotateTimer: ReturnType<typeof setTimeout> | null = null;
  private latest: { blob: Blob; mime: string } | null = null;
  private firstCycleLogged = false;

  private deathClipHistory: Array<{
    blob: Blob;
    mime: string;
    killer: string;
    timestamp: number;
    arena: string;
  }> = [];
  private lastBlobUrl: string | null = null;
  private killerName = '';
  private weaponName = '';
  private respawnSec = 0;
  private lastArenaLabel: string | null = null;
  private lastMatchElapsedSec: number | null = null;
  // Called from the share path to surface "Clip opened in tab" to
  // the player. Wired by app.ts to hud.showToast once HUD exists.
  private onToast: ((msg: string) => void) | null = null;
  // The SHARE button is visible from the instant the deathcam mounts
  // so the player can click it even before the clip has finished
  // building. `clipReady` flips true once buildAndPlayClip succeeds;
  // `pendingShareClick` flags an early click that should fire as soon
  // as the clip is ready.
  private clipReady = false;
  private pendingShareClick = false;

  constructor(parent: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'ui interactive';
    this.el.style.cssText = `
      position: fixed; inset: 0;
      pointer-events: none;
      display: none;
      z-index: 80;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: 10px;
      font: 900 14px system-ui, sans-serif;
      color: #fecaca;
      background: radial-gradient(circle at center, rgba(127,29,29,0) 0%, rgba(127,29,29,.55) 70%, rgba(15,23,42,.92) 100%);
    `;

    // Respawn countdown is INDEPENDENT of the deathcam overlay —
    // mounted on the app root with fixed positioning so it still
    // shows during the ~1 s capture delay BEFORE the overlay paints
    // and keeps showing even if the recap is hidden. Driven by
    // `setRespawnSec()` from the main render loop.
    this.respawnLabel = document.createElement('div');
    this.respawnLabel.className = 'ui';
    // Positioned JUST ABOVE the deathcam frame. Frame is centered
    // vertically and is 16:9 at `min(620px, 88vw)` wide. The label
    // offset uses `calc(50vh - half-frame-height - gap)` so it
    // always sits a few px above the video's top edge regardless of
    // viewport width (clamped with min() so it doesn't go too high
    // on phones with very wide frames). Falls back to a sane top
    // value on tiny viewports.
    this.respawnLabel.style.cssText = `
      position: fixed;
      left: 50%;
      /* Anchor just above the centered video frame. Half-frame
         height = min(620px, 88vw) * 9 / 16 / 2 = min(620, 88vw) * 0.28125.
         Plus a 32 px gap. Clamped at 16 px min for tiny viewports. */
      top: max(16px, calc(50vh - min(620px, 88vw) * 0.28125 - 32px));
      transform: translate(-50%, -100%);
      font: 900 17px system-ui, sans-serif;
      letter-spacing: 5px;
      color: #fca5a5;
      text-shadow: 0 0 14px rgba(239,68,68,.55);
      padding: 8px 22px;
      border: 2px solid rgba(239,68,68,.65);
      border-radius: 999px;
      background: rgba(15,23,42,.85);
      box-shadow: 0 0 22px rgba(239,68,68,.35);
      z-index: 90;
      pointer-events: none;
      display: none;
      white-space: nowrap;
    `;
    this.respawnLabel.textContent = '';
    parent.appendChild(this.respawnLabel);

    const frame = document.createElement('div');
    // Removed the inset red shadow (`inset 0 0 28px rgba(127,29,29,.5)`)
    // — it was darkening the video edges and making the clip feel
    // low-res. Outer glow stays for the "death" framing.
    frame.style.cssText = `
      position: relative;
      width: min(620px, 88vw);
      aspect-ratio: 16 / 9;
      border: 2px solid #ef4444;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 0 48px rgba(239,68,68,.45);
      background: radial-gradient(circle at center, #1a0b0b 0%, #0b0303 80%);
      display: none;
    `;
    this.videoEl = document.createElement('video');
    this.videoEl.muted = true;
    this.videoEl.autoplay = true;
    this.videoEl.loop = true;
    this.videoEl.playsInline = true;
    this.videoEl.addEventListener('error', () => {
      const err = this.videoEl.error;
      console.warn('[recap] video error', {
        code: err?.code,
        message: err?.message,
        networkState: this.videoEl.networkState,
        readyState: this.videoEl.readyState,
      });
      this.setStatus(`DECODE FAILED · code ${err?.code} · ${(err?.message ?? '').slice(0, 60)}`);
      if (this.statusEl) this.statusEl.style.display = 'flex';
    });
    this.videoEl.addEventListener('loadedmetadata', () => {
      const dur = this.videoEl.duration;
      console.info('[recap] video loadedmetadata', {
        duration: dur,
        videoWidth: this.videoEl.videoWidth,
        videoHeight: this.videoEl.videoHeight,
      });
      if (this.videoEl.videoWidth === 0) {
        this.setStatus('DECODE LOADED 0×0 · blank frames');
        if (this.statusEl) this.statusEl.style.display = 'flex';
      }
    });
    // Rotating cycles emit self-contained ~5 s blobs from `stop()`,
    // so no trim / seek is needed. The <video> just plays the whole
    // clip end-to-end and loops with the native `loop` attribute.
    this.videoEl.loop = true;
    this.videoEl.style.cssText = `
      position: absolute; inset: 0;
      width: 100%; height: 100%;
      object-fit: cover;
      filter: saturate(1.4) contrast(1.05);
    `;
    // Softer red tint so the footage stays crisp. Old `.35/.25` made
    // low-contrast gameplay look like muddy VHS.
    const tint = document.createElement('div');
    tint.style.cssText = `
      position: absolute; inset: 0;
      background: linear-gradient(180deg, rgba(127,29,29,.15), rgba(239,68,68,.10));
      mix-blend-mode: multiply;
      pointer-events: none;
    `;
    // Scanlines removed — added visible noise to already-modest
    // bitrate video. With bitrate now at 6 Mbps the raw footage
    // reads as a clean replay.
    const status = document.createElement('div');
    status.style.cssText = `
      position: absolute; inset: 0;
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      gap: 8px;
      font: 900 11px ui-monospace, monospace;
      color: #fca5a5;
      letter-spacing: 2px;
      text-align: center;
      padding: 14px;
      pointer-events: none;
    `;
    status.textContent = 'REPLAY…';
    this.statusEl = status;
    frame.appendChild(this.videoEl);
    frame.appendChild(tint);
    // (scanlines removed — see comment above)
    frame.appendChild(status);
    this.el.appendChild(frame);

    this.titleBlock = document.createElement('div');
    this.titleBlock.style.cssText = `
      display: flex; flex-direction: column; align-items: center; gap: 6px;
      transition: opacity 0.3s ease-out;
    `;
    this.el.appendChild(this.titleBlock);

    this.shareBtn = document.createElement('button');
    this.shareBtn.className = 'ui interactive';
    this.shareBtn.textContent = '📎 DOWNLOAD IN NEW TAB';
    this.shareBtn.style.cssText = `
      margin-top: 6px;
      padding: 8px 22px;
      background: rgba(15,23,42,.85);
      border: 2px solid #fde047;
      border-radius: 8px;
      color: #fde047;
      font: 900 12px system-ui;
      letter-spacing: 2px;
      cursor: pointer;
      pointer-events: auto;
      display: none;
      box-shadow: 0 0 24px rgba(253,224,71,.35);
      transition: opacity 0.3s ease-out;
    `;
    this.shareBtn.addEventListener('click', () => this.onShareClick());
    this.el.appendChild(this.shareBtn);

    parent.appendChild(this.el);
    (this.el as HTMLDivElement & { __frame?: HTMLDivElement }).__frame = frame;
  }

  /** Wire the hud toast so the share flow can notify "opened in tab". */
  setToastHook(cb: (msg: string) => void): void {
    this.onToast = cb;
  }

  /** Hook up the capture source. Starts the first cycle. */
  attachToCanvas(canvas: HTMLCanvasElement): boolean {
    if (this.mime && this.activeRecorder) return true;
    this.canvasRef = canvas;
    this.mime = pickMime();
    if (!this.mime) {
      console.info('[recap] MediaRecorder unsupported — falling back to text-only');
      return false;
    }
    return this.startCycle();
  }

  /**
   * Start a fresh rotating cycle. Cycles auto-rotate every CYCLE_MS,
   * caching the completed blob as `this.latest`. On death, a
   * force-rotate triggered from `buildAndPlayClip()` stops the active
   * cycle early so the blob ends AT the post-death capture window.
   */
  private startCycle(): boolean {
    if (!this.canvasRef || !this.mime) return false;
    const mime = this.mime;
    try {
      // 15 fps capture — plenty for replay playback (we don't need
      // 60 fps of historical frames), halves the encode CPU cost vs
      // the old 30 fps setting. Replays still look smooth in-frame.
      const stream = (this.canvasRef as HTMLCanvasElement & { captureStream?: (fps: number) => MediaStream }).captureStream?.(15);
      if (!stream) return false;
      // 3 Mbps balances crispness vs CPU: two MediaRecorders running
      // in parallel (deathcam + moment recorder) at 6 Mbps each made
      // the game jank — 3 Mbps is still sharp at 720p and cuts the
      // encode cost roughly in half.
      const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 3_000_000 });
      const parts: Blob[] = [];
      const startedAt = performance.now();
      recorder.addEventListener('dataavailable', (e) => {
        if (e.data.size > 0) parts.push(e.data);
      });
      recorder.addEventListener('stop', () => {
        const durMs = performance.now() - startedAt;
        if (parts.length === 0) {
          // Kick off the next cycle even on empty — keeps the loop alive.
          if (this.activeRecorder === recorder) {
            this.activeRecorder = null;
            this.startCycle();
          }
          return;
        }
        const blob = new Blob(parts, { type: mime });
        // Self-contained WebM from stop() — duration metadata is
        // baked in, so playback trims itself to clip length.
        if (blob.size >= 4096 && durMs >= 400) {
          this.latest = { blob, mime };
          if (!this.firstCycleLogged) {
            this.firstCycleLogged = true;
            console.info('[recap] first cycle complete', { size: blob.size, durMs, mime });
          }
        }
        if (this.activeRecorder === recorder) {
          this.activeRecorder = null;
          this.startCycle();
        }
      });
      recorder.start();
      this.activeRecorder = recorder;
      this.activeParts = parts;
      this.activeStartMs = startedAt;
      this.scheduleRotation();
      return true;
    } catch (err) {
      console.warn('[recap] cycle start failed', err);
      return false;
    }
  }

  private scheduleRotation(): void {
    if (this.rotateTimer) clearTimeout(this.rotateTimer);
    this.rotateTimer = setTimeout(() => {
      this.rotateTimer = null;
      if (this.activeRecorder) {
        try { this.activeRecorder.stop(); } catch { /* ignore */ }
      }
    }, CYCLE_MS);
  }

  /**
   * Force-rotate NOW: stop the active cycle, wait for its `stop`
   * event so `this.latest` is populated, and return. Used on death
   * after the POST_DEATH_CAPTURE_MS wait so the cached blob ends at
   * the death frame + 1 s rather than up to CYCLE_MS earlier.
   */
  private async forceRotate(): Promise<void> {
    const rec = this.activeRecorder;
    if (!rec) return;
    if (this.rotateTimer) { clearTimeout(this.rotateTimer); this.rotateTimer = null; }
    return new Promise<void>((resolve) => {
      const handler = () => {
        rec.removeEventListener('stop', handler);
        resolve();
      };
      rec.addEventListener('stop', handler);
      try { rec.stop(); } catch { resolve(); }
    });
  }

  /**
   * Called at warmup→playing AND after each respawn. Tears down the
   * current cycle without caching its blob (the prior life is either
   * irrelevant or already displayed), then starts a fresh one so the
   * next death captures from T=0 of the new life.
   */
  restart(): void {
    if (this.rotateTimer) { clearTimeout(this.rotateTimer); this.rotateTimer = null; }
    if (this.activeRecorder) {
      const rec = this.activeRecorder;
      this.activeRecorder = null; // prevents onstop from auto-restarting
      try { rec.stop(); } catch { /* ignore */ }
      this.activeParts = [];
    }
    if (this.mime && this.canvasRef) this.startCycle();
  }

  isRecording(): boolean {
    return this.mime !== null && this.activeRecorder !== null;
  }

  /**
   * Overall pipeline live? Used by app.ts to decide whether to show
   * the replay frame or fall through to the text-only fallback.
   */
  hasPipeline(): boolean {
    return this.mime !== null;
  }

  show(
    killer: string,
    weapon: string,
    localRoleLabel: string,
    meta?: { arenaLabel?: string; matchElapsedSec?: number; respawnSec?: number },
  ): void {
    this.killerName = killer;
    this.weaponName = weapon;
    this.lastArenaLabel = meta?.arenaLabel ?? null;
    this.lastMatchElapsedSec = meta?.matchElapsedSec ?? null;
    this.respawnSec = meta?.respawnSec ?? 0;
    this.el.style.display = 'flex';
    this.timer = SHOW_DURATION_SEC;
    this.duration = this.timer;
    this.renderTitle(killer, weapon, localRoleLabel, this.lastArenaLabel, this.lastMatchElapsedSec);
    this.titleBlock.style.opacity = '1';
    this.el.style.opacity = '0';
    // SHARE button mounts immediately — clicks during the capture
    // wait defer via `pendingShareClick`. Reset state for this death.
    this.clipReady = false;
    this.pendingShareClick = false;
    this.shareBtn.style.display = 'inline-block';
    this.shareBtn.style.opacity = '1';
    this.shareBtn.disabled = false;
    this.shareBtn.textContent = '📎 DOWNLOAD IN NEW TAB';
    requestAnimationFrame(() => {
      this.el.style.transition = 'opacity .22s ease-out';
      this.el.style.opacity = '1';
    });

    // Frame always visible on death.
    const frame = (this.el as HTMLDivElement & { __frame?: HTMLDivElement }).__frame;
    if (frame) frame.style.display = 'block';
    if (this.statusEl) this.statusEl.style.display = 'flex';
    this.setStatus(this.mime ? 'CAPTURING FINAL MOMENT…' : 'REPLAY UNAVAILABLE');

    // Respawn label is driven separately via setRespawnSec from the
    // render loop; no initial-frame wiring needed here.

    // Delay the stop-and-play by POST_DEATH_CAPTURE_MS so ~1 s of
    // post-death frames (kill stamp, ragdoll) end up in the blob.
    if (this.mime) {
      setTimeout(() => {
        this.buildAndPlayClip().catch((err) => {
          console.warn('[recap] clip build failed', err);
          this.setStatus(`REPLAY FAILED: ${String(err).slice(0, 80)}`);
        });
      }, POST_DEATH_CAPTURE_MS);
    }
  }

  /**
   * Called by app.ts render loop with the server's respawnInSec.
   * Visibility is driven purely by sec > 0 — the label lives
   * independently of the deathcam overlay so it still shows during
   * the 1 s capture delay before the video frame paints, and stays
   * visible through death even if the recap is hidden.
   *
   * Display uses .1 resolution (e.g. "RESPAWN IN 4.3s") so the
   * countdown visibly moves every frame instead of jumping by whole
   * seconds. rAF fires ~60 Hz, so `sec` is fractional every call.
   */
  setRespawnSec(sec: number): void {
    this.respawnSec = sec;
    if (sec > 0.05) {
      // Round DOWN to 1 decimal so the number never jumps above the
      // true remaining time (ceil would display '5.0' when 4.95 is
      // the true value — bad vibes for the "respawn is about to
      // fire" feel).
      const shown = Math.max(0, Math.floor(sec * 10) / 10).toFixed(1);
      this.respawnLabel.textContent = `RESPAWN IN ${shown}s`;
      this.respawnLabel.style.display = 'block';
    } else {
      this.respawnLabel.style.display = 'none';
    }
  }

  getDeathClipHistory(): ReadonlyArray<{
    blob: Blob;
    mime: string;
    killer: string;
    timestamp: number;
    arena: string;
  }> {
    return this.deathClipHistory;
  }

  private setStatus(text: string): void {
    if (this.statusEl) this.statusEl.textContent = text;
  }

  getLastContext(): { arenaLabel: string | null; matchElapsedSec: number | null } {
    return { arenaLabel: this.lastArenaLabel, matchElapsedSec: this.lastMatchElapsedSec };
  }

  /**
   * Call `videoEl.play()` but handle Chrome's "video-only background
   * media was paused to save power" AbortError. If the tab is
   * backgrounded when the deathcam mounts, the muted autoplay gets
   * paused mid-load — we register a one-shot visibilitychange listener
   * that retries play() the moment the user tabs back.
   */
  private async playWithBackgroundRetry(): Promise<void> {
    try {
      await this.videoEl.play();
      this.setStatus('');
      if (this.statusEl) this.statusEl.style.display = 'none';
    } catch (err) {
      const name = (err as { name?: string })?.name ?? '';
      if (name === 'AbortError' || name === 'NotAllowedError') {
        // Chrome auto-paused the video (tab backgrounded) OR autoplay
        // policy requires a user gesture. Retry when either condition
        // clears. No status update — we don't want to scare the user.
        console.info('[recap] play deferred — retry on visibility/gesture', { name });
        const tryAgain = () => {
          this.videoEl.play().then(() => {
            this.setStatus('');
            if (this.statusEl) this.statusEl.style.display = 'none';
            document.removeEventListener('visibilitychange', onVisible);
            document.removeEventListener('pointerdown', onGesture);
          }).catch(() => { /* keep waiting */ });
        };
        const onVisible = () => { if (document.visibilityState === 'visible') tryAgain(); };
        const onGesture = () => tryAgain();
        document.addEventListener('visibilitychange', onVisible);
        document.addEventListener('pointerdown', onGesture, { once: true });
      } else {
        console.warn('[recap] video play() rejected', err);
        this.setStatus(`PLAY REJECTED · ${String(err).slice(0, 80)}`);
      }
    }
  }

  private renderTitle(
    killer: string,
    weapon: string,
    roleLabel: string,
    arenaLabel: string | null,
    elapsedSec: number | null,
  ): void {
    const ctxLine = formatContextLine(arenaLabel, elapsedSec);
    this.titleBlock.innerHTML = `
      <div style="
        font: 900 11px system-ui; letter-spacing: 5px;
        color: #fecaca; opacity: .7;
      ">YOU WERE</div>
      <div style="
        font: 900 56px system-ui; letter-spacing: 4px;
        background: linear-gradient(90deg,#f87171,#fca5a5,#ef4444);
        -webkit-background-clip: text; background-clip: text; color: transparent;
        text-shadow: 0 0 36px rgba(239,68,68,.55);
        margin: 2px 0;
      ">ELIMINATED</div>
      <div style="
        font: 800 14px system-ui; letter-spacing: 2px; opacity: .9;
        color: #fecdd3;
      ">BY <span style="color:#fde047;">${escapeHtml(killer)}</span></div>
      <div style="
        font: 700 10px system-ui; letter-spacing: 2px; opacity: .55;
        color: #e5e7eb;
      ">WEAPON: ${escapeHtml(weapon)} · ${escapeHtml(roleLabel)}</div>
      ${ctxLine ? `<div style="
        font: 700 10px system-ui; letter-spacing: 2px; opacity: .7;
        color: #fde047; margin-top: 2px;
      ">${ctxLine}</div>` : ''}
    `;
  }

  /**
   * On-demand grab of the most recent moment-worthy clip. Now used
   * ONLY by legacy callers — the MVP reel pipeline has its own
   * rotating recorder in MomentRecorder. Kept for backward compat
   * with the sudden-death "YOUR LAST MOMENT" inset path which still
   * wants the death clip during finale Part A. Returns null until
   * the player dies once — there's no rolling blob mid-life.
   */
  async getLatestClipBlob(): Promise<{ blob: Blob; mime: string } | null> {
    // Prefer the most recently assembled history entry (the last
    // completed death). If the player is still on their first life,
    // history is empty; return null instead of blocking on a stop().
    if (this.deathClipHistory.length === 0) return null;
    const last = this.deathClipHistory[this.deathClipHistory.length - 1];
    return { blob: last.blob, mime: last.mime };
  }

  private async buildAndPlayClip(): Promise<void> {
    if (!this.mime) {
      console.info('[recap] no recorder — text-only');
      this.setStatus('REPLAY UNAVAILABLE · no MediaRecorder');
      return;
    }
    // Force-rotate the active cycle so `this.latest` gets the blob
    // that ends at death + POST_DEATH_CAPTURE_MS. Cycle onstop also
    // auto-starts the next cycle, so we're ready for the next life.
    await this.forceRotate();
    const payload = this.latest;
    const frame = (this.el as HTMLDivElement & { __frame?: HTMLDivElement }).__frame;
    if (!frame) return;
    if (!payload || payload.blob.size < 4096) {
      this.setStatus('NO CLIP — cycle too short');
      return;
    }
    console.info('[recap] playing clip', { size: payload.blob.size, mime: payload.mime });
    this.setStatus(`LOADED · ${(payload.blob.size / 1024).toFixed(1)} KB`);

    // DO NOT revoke the previous `lastBlobUrl` here — if a share
    // tab opened on the previous death hasn't finished fetching the
    // blob yet, revoking would kill it. Delay revocation by 60 s so
    // the opened tab has time to clone the blob into its own scope.
    if (this.lastBlobUrl) {
      const stale = this.lastBlobUrl;
      setTimeout(() => { try { URL.revokeObjectURL(stale); } catch { /* ignore */ } }, 60_000);
    }
    const url = URL.createObjectURL(payload.blob);
    this.lastBlobUrl = url;
    this.videoEl.src = url;
    // Real-time playback — slow-mo removed per user feedback.
    this.videoEl.playbackRate = 1.0;
    await this.playWithBackgroundRetry();
    // Clip is now playable — any queued early share click fires now.
    this.clipReady = true;
    if (this.pendingShareClick) {
      this.pendingShareClick = false;
      void this.onShareClick();
    }

    this.deathClipHistory.push({
      blob: payload.blob,
      mime: payload.mime,
      killer: this.killerName,
      timestamp: Date.now(),
      arena: this.lastArenaLabel ?? 'unknown',
    });
    while (this.deathClipHistory.length > HISTORY_MAX) this.deathClipHistory.shift();
  }

  private async onShareClick(): Promise<void> {
    // Clicked before the clip has finished building? Defer — we'll
    // auto-fire once `buildAndPlayClip()` flips `clipReady`.
    if (!this.clipReady || !this.lastBlobUrl) {
      this.pendingShareClick = true;
      this.shareBtn.textContent = '📎 DOWNLOAD IN NEW TAB';
      this.shareBtn.style.color = '#fde047';
      return;
    }
    const ext = this.mime ? extForMime(this.mime) : 'webm';
    const filename = buildClipFilename('death', this.killerName, ext);
    await shareClip({
      blobUrl: this.lastBlobUrl,
      filename,
      shareText: `I got ratio'd by ${this.killerName} in VIRGIN VS CHAD — ${this.weaponName}`,
      onStatus: (msg, color) => this.flashShareLabel(msg, color),
      onToast: this.onToast ?? undefined,
    });
  }

  private flashShareLabel(msg: string, color: string): void {
    const original = this.shareBtn.textContent;
    const origColor = this.shareBtn.style.color;
    this.shareBtn.textContent = msg;
    this.shareBtn.style.color = color;
    this.shareBtn.style.borderColor = color;
    setTimeout(() => {
      this.shareBtn.textContent = original;
      this.shareBtn.style.color = origColor;
      this.shareBtn.style.borderColor = '#fde047';
    }, 1400);
  }

  update(dt: number): void {
    if (this.timer <= 0) return;
    this.timer -= dt;
    const elapsed = this.duration - this.timer;
    // Text-block fade: fully visible until TEXT_FADE_START_SEC, then
    // linearly fades to 0 at TEXT_FADE_END_SEC. Video keeps playing
    // behind the fading text for a clean screenshot frame. Share
    // button stays full opacity for the whole recap — excluded from
    // the fade so the player can always click it.
    if (elapsed >= TEXT_FADE_START_SEC && elapsed < TEXT_FADE_END_SEC) {
      const k = (elapsed - TEXT_FADE_START_SEC) / (TEXT_FADE_END_SEC - TEXT_FADE_START_SEC);
      const opacity = Math.max(0, 1 - k);
      this.titleBlock.style.opacity = String(opacity);
    } else if (elapsed >= TEXT_FADE_END_SEC) {
      this.titleBlock.style.opacity = '0';
    }
    // Overall overlay fades in the final 0.4 s.
    if (this.timer <= 0.4 && this.el.style.opacity !== '0') {
      this.el.style.transition = 'opacity .35s ease-in';
      this.el.style.opacity = '0';
    }
    if (this.timer <= 0) {
      this.el.style.display = 'none';
      const frame = (this.el as HTMLDivElement & { __frame?: HTMLDivElement }).__frame;
      if (frame) frame.style.display = 'none';
      this.shareBtn.style.display = 'none';
      try { this.videoEl.pause(); } catch { /* ignore */ }
      // respawnLabel visibility is driven by setRespawnSec — leaving
      // it alone here lets the countdown keep ticking independent of
      // the deathcam overlay timer.
    }
  }

  hide(): void {
    this.timer = 0;
    this.el.style.display = 'none';
    const frame = (this.el as HTMLDivElement & { __frame?: HTMLDivElement }).__frame;
    if (frame) frame.style.display = 'none';
    this.shareBtn.style.display = 'none';
    try { this.videoEl.pause(); } catch { /* ignore */ }
    // Respawn label deliberately left untouched — owned by
    // setRespawnSec which is driven each frame by local.respawnInSec.
  }

  /**
   * Hide the overlay but guarantee at least `minVisibleMs` of on-
   * screen time since show(). Bumped to 4 s (was 1.2 s) so the
   * deathcam has time to actually build+play the clip before a
   * fast-arriving matchResult (e.g. level-race end) stomps it.
   */
  hideAfterGrace(minVisibleMs = 4000): void {
    if (this.timer <= 0) { this.hide(); return; }
    const elapsedMs = Math.max(0, (this.duration - this.timer) * 1000);
    if (elapsedMs >= minVisibleMs) { this.hide(); return; }
    const wait = minVisibleMs - elapsedMs;
    setTimeout(() => this.hide(), wait);
  }
}

function formatContextLine(arenaLabel: string | null, elapsedSec: number | null): string {
  const parts: string[] = [];
  if (arenaLabel && arenaLabel.length > 0) parts.push(`@ ${escapeHtml(arenaLabel.toUpperCase())}`);
  if (typeof elapsedSec === 'number' && isFinite(elapsedSec) && elapsedSec >= 0) {
    const m = Math.floor(elapsedSec / 60);
    const s = Math.floor(elapsedSec % 60).toString().padStart(2, '0');
    parts.push(`T+${m}:${s}`);
  }
  return parts.join(' · ');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c] as string));
}

export function buildClipFilename(
  kind: 'death' | 'mvp' | 'moment',
  label: string,
  ext: string,
): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `T${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const slug = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24) || 'anon';
  return `virgin-vs-chad-${kind}-${stamp}-${slug}.${ext}`;
}
